// The Product Control Plane document contract — the CLIENT side.
//
// This is a deliberate re-implementation of `gao-id-worker`'s
// `src/lib/control-plane.contract.ts`, not an import of it. Server-side
// filtering and client-side filtering are defence in depth, and the whole point
// of defence in depth is that the second implementation is independent: a
// shared module would fail in both places at once.
//
// What that buys is only real if the two agree, so the agreement is tested
// against the SAME vectors the server runs —
// `gao-id-worker/tests/fixtures/control-plane/{version-comparison,visibility}.json`,
// copied into `src/controlPlane/fixtures/`. When the server's vectors change,
// this file is what the diff makes you look at.
//
// THE INVARIANT, restated here because this is the file a future edit would
// have to get past:
//
//     THE CONTROL PLANE NEVER SELECTS AN APPLICATION'S BACKEND HOST OR LANE.
//
// There is no host field in this contract, and `FORBIDDEN_ITEM_KEYS` refuses
// one by name.

import {
  COMPONENT_TYPE_LOCAL_AI_MODELS,
  modelRowProblems,
  type LocalAIModelItem,
  type LocalAIModelsComponent,
} from "./localAI";

export const SCHEMA_VERSION = 1 as const;
export const COMPONENT_TYPE_CATALOG_GRID = "catalog_grid" as const;
export const ITEM_ENVIRONMENTS = ["production", "staging", "test"] as const;
export const LANES = ["production", "test", "dev"] as const;

export type ItemEnvironment = (typeof ITEM_ENVIRONMENTS)[number];
export type Lane = (typeof LANES)[number];

export interface CatalogItem {
  id: string;
  title: string;
  description: string | null;
  category: string | null;
  url: string;
  environment: ItemEnvironment;
  enabled: boolean;
  sortOrder: number;
  iconUrl: string | null;
  requiresAuth: boolean;
  minAppVersion: string | null;
}

export interface CatalogGridComponent {
  type: typeof COMPONENT_TYPE_CATALOG_GRID;
  id: string;
  items: CatalogItem[];
}

/** `catalog_grid` for every product but `gao-local-ai`, whose one component is
 *  `local_ai_models` (`localAI.ts`; the server's CONTROL_PLANE_SCHEMA_V1 §9). */
export type Component = CatalogGridComponent | LocalAIModelsComponent;

export interface Surface {
  id: string;
  components: Component[];
}

export interface ControlPlaneDocument {
  schemaVersion: number;
  revision: string;
  publishedAt: string;
  productId: string;
  surfaces: Surface[];
}

export const LIMITS = {
  itemsPerComponent: 200,
  title: 120,
  description: 500,
  category: 64,
  url: 2048,
  iconUrl: 2048,
  minAppVersion: 32,
  sortOrder: 1_000_000,
} as const;

export const FORBIDDEN_ITEM_KEYS = [
  "apiHost",
  "baseHost",
  "resolverHost",
  "laneHost",
  "host",
  "backendOverride",
  "environmentOverride",
  "lane",
  "script",
  "html",
  "css",
  "theme",
  "action",
  "nativeAction",
  "js",
  "javascript",
] as const;

const ID_RE = /^[a-z0-9][a-z0-9-]{0,63}$/;
// eslint-disable-next-line no-control-regex
const CONTROL_CHAR_RE = /[\u0000-\u001F\u007F-\u009F]/;

// ── Version comparison ──────────────────────────────────────────────────────
//
// Six digits per segment, no leading zeros. Both bounds are load-bearing and
// neither is taste: GaoKey Native parses this same field with
// `^\d{1,6}(\.\d{1,6}){0,3}$` and drops the WHOLE ROW on a mismatch, so a
// seven-digit segment would publish a row a shipping Product client silently
// deletes. Leading zeros are refused because "01" and "1" comparing equal while
// sorting differently is a bug waiting for a maintenance window.
//
// A string comparison would be wrong in the direction that matters:
// "1.0.10" < "1.0.9" lexicographically, which hides an entry from exactly the
// newer builds it was meant for.

const VERSION_RE = /^(0|[1-9]\d{0,5})(\.(0|[1-9]\d{0,5})){0,3}$/;
const VERSION_SEGMENTS = 4;

export function parseVersion(raw: unknown): number[] | null {
  if (typeof raw !== "string") return null;
  if (raw.length === 0 || raw.length > LIMITS.minAppVersion) return null;
  if (!VERSION_RE.test(raw)) return null;
  const parts = raw.split(".").map((s) => Number.parseInt(s, 10));
  if (parts.some((n) => !Number.isSafeInteger(n) || n < 0)) return null;
  while (parts.length < VERSION_SEGMENTS) parts.push(0);
  return parts;
}

export function compareVersions(a: unknown, b: unknown): -1 | 0 | 1 | null {
  const pa = parseVersion(a);
  const pb = parseVersion(b);
  if (pa === null || pb === null) return null;
  for (let i = 0; i < VERSION_SEGMENTS; i++) {
    const left = pa[i] ?? 0;
    const right = pb[i] ?? 0;
    if (left < right) return -1;
    if (left > right) return 1;
  }
  return 0;
}

// ── Visibility ──────────────────────────────────────────────────────────────

const LANE_ALLOWED: Record<Lane, ReadonlySet<ItemEnvironment>> = {
  production: new Set<ItemEnvironment>(["production"]),
  test: new Set<ItemEnvironment>(["production", "staging", "test"]),
  dev: new Set<ItemEnvironment>(["production", "staging", "test"]),
};

export type HiddenReason =
  | "disabled"
  | "environment_mismatch"
  | "environment_malformed"
  | "min_app_version_malformed"
  | "client_version_unknown"
  | "client_version_below_minimum";

export type VisibilityDecision =
  | { visible: true }
  | { visible: false; reason: HiddenReason };

/** Human text for a hidden reason — the console must always be able to say WHY
 *  a row will not render, not merely that it will not. */
export const HIDDEN_REASON_TEXT: Record<HiddenReason, string> = {
  disabled: "disabled",
  environment_mismatch: "environment not served on this lane",
  environment_malformed: "environment value is not recognised",
  min_app_version_malformed: "minAppVersion is malformed",
  client_version_unknown: "app version not declared",
  client_version_below_minimum: "app version is below minAppVersion",
};

export function decideItemVisibility(
  item: CatalogItem,
  lane: Lane,
  clientVersion: string | null,
): VisibilityDecision {
  if (item.enabled !== true) return { visible: false, reason: "disabled" };

  if (!(ITEM_ENVIRONMENTS as readonly string[]).includes(item.environment)) {
    return { visible: false, reason: "environment_malformed" };
  }
  if (!LANE_ALLOWED[lane].has(item.environment)) {
    return { visible: false, reason: "environment_mismatch" };
  }

  if (item.minAppVersion !== null) {
    if (parseVersion(item.minAppVersion) === null) {
      return { visible: false, reason: "min_app_version_malformed" };
    }
    if (clientVersion === null) {
      return { visible: false, reason: "client_version_unknown" };
    }
    const cmp = compareVersions(clientVersion, item.minAppVersion);
    if (cmp === null) return { visible: false, reason: "client_version_unknown" };
    if (cmp < 0) return { visible: false, reason: "client_version_below_minimum" };
  }

  return { visible: true };
}

// ── URL policy ──────────────────────────────────────────────────────────────
//
// The credential check is the one a protocol-only test misses:
// `new URL("https://user:pass@evil.example").protocol` IS "https:", so a
// scheme check alone accepts a URL that hands a credential to whatever host it
// names.

export type UrlFailure =
  | "required"
  | "too_long"
  | "control_characters"
  | "unparseable"
  | "not_https"
  | "embedded_credentials"
  | "no_host";

export const URL_FAILURE_TEXT: Record<UrlFailure, string> = {
  required: "required",
  too_long: "too long",
  control_characters: "contains control characters",
  unparseable: "is not a valid URL",
  not_https: "must be https://",
  embedded_credentials: "must not embed a username or password",
  no_host: "has no host",
};

export function checkHttpsUrl(raw: unknown, maxLength: number): UrlFailure | null {
  if (typeof raw !== "string" || raw.length === 0) return "required";
  if (raw.length > maxLength) return "too_long";
  if (CONTROL_CHAR_RE.test(raw)) return "control_characters";
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    return "unparseable";
  }
  if (u.protocol !== "https:") return "not_https";
  if (u.username !== "" || u.password !== "") return "embedded_credentials";
  if (u.hostname === "") return "no_host";
  return null;
}

// ── Row validation (what the editor shows inline, before publish) ───────────

export interface FieldIssue {
  itemId: string;
  field: keyof CatalogItem | "row";
  message: string;
}

export function validateItem(item: CatalogItem): FieldIssue[] {
  const out: FieldIssue[] = [];
  const push = (field: FieldIssue["field"], message: string) =>
    out.push({ itemId: item.id, field, message });

  if (typeof item.id !== "string" || !ID_RE.test(item.id)) {
    push("id", "lowercase letters, digits and dashes only, max 64");
  }
  if (typeof item.title !== "string" || item.title.trim() === "") {
    push("title", "required");
  } else if (item.title.length > LIMITS.title) {
    push("title", `max ${LIMITS.title} characters`);
  }

  const url = checkHttpsUrl(item.url, LIMITS.url);
  if (url) push("url", `url ${URL_FAILURE_TEXT[url]}`);

  if (item.iconUrl !== null && item.iconUrl !== "") {
    const icon = checkHttpsUrl(item.iconUrl, LIMITS.iconUrl);
    // A malformed icon costs the whole row on GaoKey Native, not just the
    // icon — so the console treats it as a row error, not a cosmetic warning.
    if (icon) push("iconUrl", `iconUrl ${URL_FAILURE_TEXT[icon]} — this drops the whole row on some clients`);
  }

  if (item.description !== null && item.description.length > LIMITS.description) {
    push("description", `max ${LIMITS.description} characters`);
  }
  if (item.category !== null && item.category.length > LIMITS.category) {
    push("category", `max ${LIMITS.category} characters`);
  }
  if (!(ITEM_ENVIRONMENTS as readonly string[]).includes(item.environment)) {
    push("environment", `must be one of ${ITEM_ENVIRONMENTS.join(", ")}`);
  }
  if (
    typeof item.sortOrder !== "number" ||
    !Number.isInteger(item.sortOrder) ||
    item.sortOrder < 0 ||
    item.sortOrder > LIMITS.sortOrder
  ) {
    push("sortOrder", `whole number between 0 and ${LIMITS.sortOrder}`);
  }
  if (item.minAppVersion !== null && item.minAppVersion !== "") {
    if (parseVersion(item.minAppVersion) === null) {
      push("minAppVersion", "1–4 numbers separated by dots, e.g. 1.4.0 (max 6 digits each)");
    }
  }
  return out;
}

export function validateDocumentRows(doc: ControlPlaneDocument): FieldIssue[] {
  const out: FieldIssue[] = [];
  for (const surface of doc.surfaces) {
    for (const component of surface.components) {
      if (component.type === COMPONENT_TYPE_LOCAL_AI_MODELS) {
        for (const p of modelRowProblems(component.items)) {
          out.push({ itemId: p.itemId, field: "row", message: `${p.field}: ${p.message}` });
        }
        continue;
      }
      const seen = new Set<string>();
      for (const item of component.items) {
        if (seen.has(item.id)) {
          out.push({
            itemId: item.id,
            field: "row",
            message: "duplicate id — two rows cannot claim the same identity",
          });
        }
        seen.add(item.id);
        out.push(...validateItem(item));
      }
      if (component.items.length > LIMITS.itemsPerComponent) {
        out.push({
          itemId: component.id,
          field: "row",
          message: `at most ${LIMITS.itemsPerComponent} rows`,
        });
      }
    }
  }
  return out;
}

// ── Helpers the console uses everywhere ─────────────────────────────────────

/** The catalog_grid rows of a document. A `local_ai_models` component has
 *  model rows instead (`modelItems`), which are never catalog rows. */
export function allItems(doc: ControlPlaneDocument): CatalogItem[] {
  return doc.surfaces.flatMap((s) =>
    s.components.flatMap((c) => (c.type === COMPONENT_TYPE_CATALOG_GRID ? c.items : [])),
  );
}

/** The model rows of a `gao-local-ai` document. */
export function modelItems(doc: ControlPlaneDocument): LocalAIModelItem[] {
  return doc.surfaces.flatMap((s) =>
    s.components.flatMap((c) => (c.type === COMPONENT_TYPE_LOCAL_AI_MODELS ? c.items : [])),
  );
}

/** Replace the model rows of the `local_ai_models` component, preserving everything else. */
export function withModelItems(doc: ControlPlaneDocument, componentId: string, items: LocalAIModelItem[]): ControlPlaneDocument {
  return {
    ...doc,
    surfaces: doc.surfaces.map((s) => ({
      ...s,
      components: s.components.map((c) =>
        c.id === componentId && c.type === COMPONENT_TYPE_LOCAL_AI_MODELS ? { ...c, items } : c,
      ),
    })),
  };
}

/** Replace the items of the first catalog_grid, preserving everything else.
 *  V1 has exactly one grid; this is written so a second one does not silently
 *  get clobbered when it arrives. */
export function withItems(
  doc: ControlPlaneDocument,
  componentId: string,
  items: CatalogItem[],
): ControlPlaneDocument {
  return {
    ...doc,
    surfaces: doc.surfaces.map((s) => ({
      ...s,
      components: s.components.map((c) =>
        c.id === componentId && c.type === COMPONENT_TYPE_CATALOG_GRID ? { ...c, items } : c,
      ),
    })),
  };
}

/** sortOrder ascending, ties by id — the order every consumer renders. */
export function inRenderOrder(items: CatalogItem[]): CatalogItem[] {
  return [...items].sort((a, b) =>
    a.sortOrder !== b.sortOrder
      ? a.sortOrder - b.sortOrder
      : a.id < b.id
        ? -1
        : a.id > b.id
          ? 1
          : 0,
  );
}
